import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShoppingCart, Eye, Package } from "lucide-react";
import SellerLayout, { useSellerSession } from "./layout";
import type { Client } from "@shared/schema";

interface OrderItem {
  id: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
  product: { name: string; unit?: string };
}

interface OrderWithItems {
  id: string;
  clientId: string;
  status: string;
  orderNumber: number;
  totalAmount: string | null;
  createdAt: string;
  orderDate: string;
  notes?: string | null;
  client: Client;
  items: OrderItem[];
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Ny",
  confirmed: "Bekräftad",
  in_production: "I produktion",
  packed: "Packad",
  delivered: "Levererad",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  in_production: "bg-purple-50 text-purple-700 border-purple-200",
  packed: "bg-orange-50 text-orange-700 border-orange-200",
  delivered: "bg-green-50 text-green-700 border-green-200",
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/Stockholm",
  });
}

export default function SellerOrders() {
  const { data: seller } = useSellerSession();
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);

  const { data: orders, isLoading } = useQuery<OrderWithItems[]>({
    queryKey: ["/api/seller/orders"],
    enabled: seller?.canViewOrders === true,
  });

  if (!seller?.canViewOrders) {
    return (
      <SellerLayout>
        <div className="py-24 text-center text-muted-foreground">
          <ShoppingCart className="h-12 w-12 mx-auto mb-4" />
          <p>Du har inte behörighet att se ordrar.</p>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Ordrar</h1>
          <p className="text-muted-foreground">Alla ordrar från dina kunder</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Orderhistorik</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : orders && orders.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead>Kund</TableHead>
                      <TableHead>Artiklar</TableHead>
                      <TableHead className="text-right">Totalt</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                      <TableHead className="text-right">Detaljer</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow key={order.id} data-testid={`row-order-${order.id}`}>
                        <TableCell className="font-medium">#{order.orderNumber}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(order.orderDate ?? order.createdAt)}
                        </TableCell>
                        <TableCell data-testid={`order-client-${order.id}`}>{order.client?.name ?? "—"}</TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {order.items?.length ?? 0} art.
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium" data-testid={`order-total-${order.id}`}>
                          {order.totalAmount ? `${parseFloat(order.totalAmount).toFixed(2)} kr` : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={`text-xs ${STATUS_COLORS[order.status] ?? ""}`}>
                            {STATUS_LABELS[order.status] ?? order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setSelectedOrder(order)}
                            title="Se orderdetaljer"
                            data-testid={`button-view-order-${order.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Inga ordrar ännu</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Order detail dialog (read-only) */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order #{selectedOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Kund</p>
                  <p className="font-medium">{selectedOrder.client?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Datum</p>
                  <p className="font-medium">{formatDate(selectedOrder.orderDate ?? selectedOrder.createdAt)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Status</p>
                  <Badge variant="outline" className={`text-xs mt-0.5 ${STATUS_COLORS[selectedOrder.status] ?? ""}`}>
                    {STATUS_LABELS[selectedOrder.status] ?? selectedOrder.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Totalt</p>
                  <p className="font-medium">
                    {selectedOrder.totalAmount ? `${parseFloat(selectedOrder.totalAmount).toFixed(2)} kr` : "—"}
                  </p>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="rounded-md bg-muted p-3 text-sm">
                  <p className="text-xs text-muted-foreground mb-1">Anteckning</p>
                  <p>{selectedOrder.notes}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-muted-foreground mb-2">Artiklar</p>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Artikel</TableHead>
                        <TableHead className="text-center">Antal</TableHead>
                        <TableHead className="text-right">À-pris</TableHead>
                        <TableHead className="text-right">Summa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{item.product.name}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">
                            {parseFloat(item.unitPrice).toFixed(2)} kr
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {parseFloat(item.totalPrice).toFixed(2)} kr
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">Inga artiklar</p>
                )}
              </div>

              <div className="pt-2 border-t flex justify-end">
                <Button variant="outline" onClick={() => setSelectedOrder(null)} data-testid="button-close-order-detail">
                  Stäng
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
}
